/**
 * An in-memory `BindingsPort` for the `?ui=controls` fixture and the Controls screen's tests:
 * the defaults of 08 §3 for both schemes, and the rules that matter to the screen, as the input
 * layer's store has them - a key already bound to another action of the same scheme is refused
 * with the reason, `''` clears a slot, `keys` holds only the bound slots, and `reset` restores
 * the active scheme alone.
 */

import type { BindingsPort, BoundAction, ControlScheme } from '../controls/bindingsPort';

type Table = ReadonlyArray<readonly [id: string, label: string, keys: readonly string[]]>;

const MODERN: Table = [
  ['moveUp', 'MOVE NORTH', ['KeyW', 'ArrowUp']],
  ['moveDown', 'MOVE SOUTH', ['KeyS', 'ArrowDown']],
  ['moveLeft', 'MOVE WEST', ['KeyA', 'ArrowLeft']],
  ['moveRight', 'MOVE EAST', ['KeyD', 'ArrowRight']],
  ['fire', 'FIRE SELECTED WEAPON', ['Mouse0']],
  ['useItem', 'USE SELECTED ITEM', ['Mouse2']],
  ['run', 'RUN / WALK', ['ShiftLeft', 'ShiftRight']],
  ['crawl', 'CRAWL / UPRIGHT', ['KeyC', 'ControlLeft']],
  ['interact', 'INTERACT / OPEN POD', ['KeyE']],
  ['reload', 'RELOAD', ['KeyR']],
  ['firstAid', 'FIRST AID', ['KeyF']],
  ['recall', 'RECALL THE OSPREY (HOLD)', ['KeyG']],
  ['knife', 'KNIFE (QUICK)', ['KeyV', 'Mouse1']],
  ['tactical', 'TACTICAL VIEW', ['Tab']],
];

const CLASSIC: Table = [
  ['moveUp', 'MOVE NORTH', ['ArrowUp']],
  ['moveDown', 'MOVE SOUTH', ['ArrowDown']],
  ['moveLeft', 'MOVE WEST', ['ArrowLeft']],
  ['moveRight', 'MOVE EAST', ['ArrowRight']],
  ['fire', 'FIRE BUTTON', ['Space']],
  ['useItem', 'SECOND BUTTON', ['Enter']],
  ['run', 'RUN / WALK', ['KeyR']],
  ['crawl', 'CRAWL / UPRIGHT', ['KeyC']],
  ['map', 'MAP (PAUSED)', ['KeyM']],
];

function copy(table: Table): BoundAction[] {
  return table.map(([id, label, keys]) => ({ id, label, keys: [...keys] }));
}

export function createFakeBindings(): BindingsPort {
  let scheme: ControlScheme = 'modern';
  let tables: Record<ControlScheme, BoundAction[]> = { modern: copy(MODERN), classic: copy(CLASSIC) };

  return {
    scheme: () => scheme,
    setScheme(s): void {
      scheme = s;
    },
    actions: () => tables[scheme],
    rebind(actionId, slot, code): string | null {
      const actions = tables[scheme];
      const target = actions.find((a) => a.id === actionId);
      if (target === undefined) return 'NO SUCH ACTION';
      if (code === 'Escape') return 'ESC IS RESERVED FOR PAUSE AND BACK';
      const keys = [...target.keys];
      if (code === '') {
        if (slot < keys.length) keys.splice(slot, 1);
      } else {
        const clash = actions.find((a) => a.keys.includes(code) && !(a.id === actionId && a.keys[slot] === code));
        if (clash !== undefined) return `ALREADY BOUND TO ${clash.label}`;
        // Only bound slots are held, so a slot past the end is simply the next free one.
        if (slot < keys.length) keys[slot] = code;
        else keys.push(code);
      }
      tables = { ...tables, [scheme]: actions.map((a) => (a.id === actionId ? { ...a, keys } : a)) };
      return null;
    },
    reset(): void {
      tables = { ...tables, [scheme]: copy(scheme === 'modern' ? MODERN : CLASSIC) };
    },
  };
}
